import { createContext, useContext, useEffect } from 'react';

/**
 * Tab → page Save registration (PROD-897).
 *
 * Practitioner Edit follows a per-tab save model — each tab owns its own
 * draft + mutation (Flutter `doctor_base_widget.dart` parity). The page
 * chrome's Save button needs to dispatch the active tab's save without
 * the page knowing each tab's internal shape.
 *
 * Pattern: each tab calls `useRegisterPractitionerSave` on every render
 * with its current `{ canSave, isPending, save, label }`. The page reads
 * the latest-registered handle (always one tab — the active one is the
 * only mounted tab) and wires the chrome Save button to it.
 *
 * Tabs that haven't migrated to this pattern simply don't call the hook —
 * the page Save stays disabled with the default "Save" label. No regression.
 */
export interface PractitionerSaveHandle {
  canSave: boolean;
  isPending: boolean;
  save: () => void;
  /**
   * Override the chrome Save button label for this tab. Defaults to "Save".
   * Example: Account tab passes `"Save Account Details"` for visual parity
   * with its inline button.
   */
  label?: string;
}

export interface PractitionerSaveContextValue {
  /**
   * Register the active tab's save handle. Called by the tab on every
   * render where the handle changes; cleared on unmount.
   *
   * The page renders only one tab at a time, so there is no contention.
   */
  register: (handle: PractitionerSaveHandle) => void;
  /** Clear the registration (called automatically on tab unmount). */
  unregister: () => void;
}

export const PractitionerSaveContext =
  createContext<PractitionerSaveContextValue | null>(null);

/**
 * Tabs call this once with their save handle. The handle is auto-cleared
 * when the tab unmounts.
 *
 * Pass `null` to opt out (e.g. when the tab's draft hasn't loaded yet).
 */
export function useRegisterPractitionerSave(
  handle: PractitionerSaveHandle | null,
): void {
  const ctx = useContext(PractitionerSaveContext);
  useEffect(() => {
    if (!ctx) return;
    if (handle) ctx.register(handle);
    else ctx.unregister();
    return () => ctx.unregister();
    // Re-register whenever any handle field changes. Pending state flips
    // and dirty-toggle both need the chrome to refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ctx,
    handle?.canSave,
    handle?.isPending,
    handle?.save,
    handle?.label,
  ]);
}
